import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material'
import {
  Visibility,
  AccountBalance,
  AccountBalanceWallet,
  TrendingUp,
  Send,
  HourglassEmpty,
  Cancel,
} from '@mui/icons-material'
import { formatDistanceToNow, isPast, format } from 'date-fns'

import { useAuth } from '../../hooks/useAuth'
import { useBranding } from '../../hooks/useBranding'
import { mutations } from '../../graphql'
import { Amount } from '..'
import { NotificationDialog } from '../dialog/notification-dialog'

function getButtonIcon(loading) {
  if (loading) {
    return <CircularProgress size={16} color="inherit" />
  }
  return <Visibility />
}

function getButtonLabel(loading) {
  if (loading) {
    return 'Loading...'
  }
  return 'View Fund'
}

function getResendTooltip(isTrulyExpired, isExpired) {
  if (isTrulyExpired) return 'Reissue invitation'
  if (isExpired) return 'Resend & extend invitation'
  return 'Resend invitation email'
}

export function ClientFundTile({ fund, onResendSuccess, onCancelSuccess }) {
  const navigate = useNavigate()
  const [, { impersonate }] = useAuth()
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const [requestedAnchorEl, setRequestedAnchorEl] = useState(null)
  const [initiatedAnchorEl, setInitiatedAnchorEl] = useState(null)
  const { accentColor, accentTextColor } = useBranding()

  const invite = fund.fundClaimInvite
  const isPending = invite?.statusLabel === 'pending'
  const isTrulyExpired = invite?.statusLabel === 'expired'
  const isPendingOrExpired = isPending || isTrulyExpired
  const isExpired = invite && isPending && isPast(new Date(invite.expiresAt))

  const [resendEmail] = useMutation(mutations.fundClaims.resendAdvisorFundClaimEmail, {
    onCompleted: () => {
      setResending(false)
      onResendSuccess?.('Invitation email has been resent.')
    },
    onError: (error) => {
      setResending(false)
      console.error('Failed to resend email:', error)
    },
  })

  const [reissueEmail] = useMutation(mutations.fundClaims.reissueAdvisorFundClaimEmail, {
    onCompleted: () => {
      setResending(false)
      onResendSuccess?.('Invitation has been reissued.')
    },
    onError: (error) => {
      setResending(false)
      console.error('Failed to reissue invite:', error)
    },
  })

  const [cancelInvite] = useMutation(mutations.fundClaims.cancelAdvisorFundClaimInvite, {
    onCompleted: () => {
      setCanceling(false)
      onCancelSuccess?.('Invitation has been cancelled.')
    },
    onError: () => {
      setCanceling(false)
    },
  })

  const handleResend = async (e) => {
    e.stopPropagation()
    if (!invite?.id) return
    setResending(true)
    if (isTrulyExpired) {
      await reissueEmail({ variables: { fundClaimInviteId: invite.id } })
    } else {
      await resendEmail({ variables: { fundClaimInviteId: invite.id } })
    }
  }

  const handleCancel = (e) => {
    e.stopPropagation()
    if (!invite?.id) return
    setConfirmCancelOpen(true)
  }

  const handleConfirmCancel = async () => {
    setConfirmCancelOpen(false)
    setCanceling(true)
    await cancelInvite({ variables: { fundClaimInviteId: invite.id } })
  }

  // Determine the first allowed route based on customPermissions
  const getDefaultDestination = () => {
    const cp = fund.customPermissions || {}
    const base = `/funds/${fund.id}`
    if (cp.menuGivingActivity !== false) return `${base}/activity/giving-wallet`
    if (cp.menuInvestmentActivity !== false) return `${base}/activity/investment-account`
    if (cp.menuContribute !== false) return `${base}/contribute`
    if (cp.menuDocuments !== false) return `${base}/documents`
    return base
  }

  // Handle fund access - impersonate and navigate
  const handleAccessFund = async (destinationPath = getDefaultDestination()) => {
    setLoading(true)
    try {
      // Call the loginAsFundOwner endpoint to impersonate
      const authState = JSON.parse(localStorage.getItem('givewise_auth_state') || '{}')
      const token = authState?.state?.rawToken

      const response = await fetch(`${process.env.REACT_APP_API_HOST || ''}/auth/loginAsFundOwner/${fund.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to access fund')
      }

      const data = await response.json()
      await impersonate(data.access_token)

      // Navigate to the destination page as the impersonated user
      navigate(destinationPath)
    } catch (error) {
      console.error('Error accessing fund:', error)
    } finally {
      setLoading(false)
    }
  }

  // Securities needing attention
  const requestedActions = (fund.securitiesNeedingAttention || []).filter((s) => s.state === 7)
  const initiatedActions = (fund.securitiesNeedingAttention || []).filter((s) => s.state === 6)

  const getSecurityDestination = (security) => {
    // state 7 = requested, state 6 = initiated
    if (security.state === 7)
      return `/funds/${fund.id}/contribute/initiated-contributions?contributionId=${security.id}`
    return `/funds/${fund.id}/activity/giving-wallet`
  }

  const handleRequestedBadgeClick = (e) => {
    e.stopPropagation()
    if (requestedActions.length === 1) {
      handleAccessFund(getSecurityDestination(requestedActions[0]))
    } else {
      setRequestedAnchorEl(e.currentTarget)
    }
  }

  const handleInitiatedBadgeClick = (e) => {
    e.stopPropagation()
    if (initiatedActions.length === 1) {
      handleAccessFund(getSecurityDestination(initiatedActions[0]))
    } else {
      setInitiatedAnchorEl(e.currentTarget)
    }
  }

  const handleRequestedMenuItemClick = (security) => {
    setRequestedAnchorEl(null)
    handleAccessFund(getSecurityDestination(security))
  }

  const handleInitiatedMenuItemClick = (security) => {
    setInitiatedAnchorEl(null)
    handleAccessFund(getSecurityDestination(security))
  }

  return (
    <Card
      sx={{
        height: '100%',
        width: 320,
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Client Name */}
        <Typography variant="h6" component="div" gutterBottom noWrap title={fund.ownerName}>
          {fund.ownerName}
        </Typography>

        {/* Fund Number */}
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Giving Fund Number: <strong>{fund.number}</strong>
        </Typography>

        {/* Invite Status and Requested Securities */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 2 }}>
          {isPendingOrExpired && (
            <Tooltip
              title={`Invitation sent ${
                invite.sentAt ? formatDistanceToNow(new Date(invite.sentAt), { addSuffix: true }) : 'not yet'
              }`}
            >
              <Chip
                label={isTrulyExpired ? 'Invite Expired' : 'Invited'}
                size="small"
                icon={<HourglassEmpty fontSize="small" />}
                sx={{ backgroundColor: accentColor, color: accentTextColor }}
              />
            </Tooltip>
          )}
          {requestedActions.length > 0 && (
            <>
              <Chip
                label={
                  requestedActions.length === 1 ? 'Action Required' : `${requestedActions.length} Actions Required`
                }
                size="small"
                onClick={handleRequestedBadgeClick}
                sx={{
                  backgroundColor: accentColor,
                  color: accentTextColor,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    filter: 'brightness(0.9)',
                    boxShadow: 2,
                    transform: 'translateY(-1px)',
                  },
                }}
              />
              {requestedActions.length > 1 && (
                <Menu
                  anchorEl={requestedAnchorEl}
                  open={Boolean(requestedAnchorEl)}
                  onClose={() => setRequestedAnchorEl(null)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {requestedActions.map((security) => (
                    <MenuItem
                      key={security.id}
                      onClick={() => handleRequestedMenuItemClick(security)}
                      sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                    >
                      <ListItemText
                        primary={`$${Number(security.amount || 0).toLocaleString()} - ${
                          security.description || 'Securities'
                        }`}
                        secondary={format(new Date(security.createdAt), 'MMM d, yyyy')}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}
          {initiatedActions.length > 0 && (
            <>
              <Chip
                label={
                  initiatedActions.length === 1
                    ? 'Outstanding Transaction'
                    : `${initiatedActions.length} Outstanding Transactions`
                }
                size="small"
                onClick={handleInitiatedBadgeClick}
                sx={{
                  backgroundColor: accentColor,
                  color: accentTextColor,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    filter: 'brightness(0.9)',
                    boxShadow: 2,
                    transform: 'translateY(-1px)',
                  },
                }}
              />
              {initiatedActions.length > 1 && (
                <Menu
                  anchorEl={initiatedAnchorEl}
                  open={Boolean(initiatedAnchorEl)}
                  onClose={() => setInitiatedAnchorEl(null)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {initiatedActions.map((security) => (
                    <MenuItem
                      key={security.id}
                      onClick={() => handleInitiatedMenuItemClick(security)}
                      sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                    >
                      <ListItemText
                        primary={`$${Number(security.amount || 0).toLocaleString()} - ${
                          security.description || 'Securities'
                        }`}
                        secondary={format(new Date(security.createdAt), 'MMM d, yyyy')}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Balances */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <BalanceRow
            icon={<AccountBalance fontSize="small" color="primary" />}
            label="Giving Fund Balance"
            amount={fund.totalBalance}
          />
          <BalanceRow
            icon={<AccountBalanceWallet fontSize="small" color="info" />}
            label="Giving Wallet Balance"
            amount={fund.givingWalletBalance}
          />
          <BalanceRow
            icon={<TrendingUp fontSize="small" color="success" />}
            label="Investment Account Balance"
            amount={fund.investmentWalletBalance}
          />
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => handleAccessFund()}
          disabled={loading}
          startIcon={getButtonIcon(loading)}
        >
          {getButtonLabel(loading)}
        </Button>
        {isPendingOrExpired && (
          <>
            <Tooltip title={getResendTooltip(isTrulyExpired, isExpired)}>
              <IconButton
                color="primary"
                onClick={handleResend}
                disabled={resending || canceling}
                size="small"
                sx={{ flexShrink: 0 }}
              >
                {resending ? <CircularProgress size={20} /> : <Send />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel invitation">
              <IconButton
                color="error"
                onClick={handleCancel}
                disabled={resending || canceling}
                size="small"
                sx={{ flexShrink: 0 }}
              >
                {canceling ? <CircularProgress size={20} /> : <Cancel />}
              </IconButton>
            </Tooltip>
          </>
        )}
      </CardActions>

      <NotificationDialog
        open={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
        type="alert"
        title="Cancel Invitation"
        message="Are you sure you want to cancel this invitation? This action cannot be undone."
        actions={[
          { label: 'No, Keep It', onClick: () => setConfirmCancelOpen(false) },
          { label: 'Yes, Cancel', onClick: handleConfirmCancel, variant: 'contained', color: 'error' },
        ]}
      />
    </Card>
  )
}

function BalanceRow({ icon, label, amount }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" fontWeight={500}>
        <Amount value={amount || 0} />
      </Typography>
    </Box>
  )
}
